import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import * as actionCreator from '../../actions';
import Config from '../../models/Config';

interface ConfigItemProps {
    config: Config;
}

const formatLabel = (key: string): string =>
    key
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');

const getSuffix = (key: string): string => {
    if (key === 'DEFAULT_MEDIA_DURATION') return 's';
    return '';
};

const SELECT_OPTIONS: Record<string, string[]> = {
    PREFERRED_LANGUAGE: ['es', 'en'],
};

const POSITIVE_INT_KEYS = new Set([
    'SCREEN_CONTENT_DATE_BEFORE',
]);

const ConfigItem: React.FC<ConfigItemProps> = ({ config }) => {

    const [t] = useTranslation();
    const dispatch = useDispatch();
    const isUpdating = useSelector((store: any) => store.configReducer.isUpdating);

    const [editing, setEditing] = useState(false);
    const [inputValue, setInputValue] = useState(config.value);
    const inputRef = useRef<HTMLInputElement>(null);

    const isThisUpdating = isUpdating === config.key;
    const isEditable = config.editable === 1;
    const suffix = getSuffix(config.key);
    const selectOptions = SELECT_OPTIONS[config.key] ?? null;
    const isPositiveInt = POSITIVE_INT_KEYS.has(config.key);

    const handleEdit = () => {
        setInputValue(config.value);
        setEditing(true);
        setTimeout(() => inputRef.current?.focus(), 30);
    };

    const handleSave = () => {
        dispatch(actionCreator.configUpdate(config.key, inputValue));
        setEditing(false);
    };

    const handleCancel = () => {
        setEditing(false);
        setInputValue(config.value);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') handleSave();
        if (e.key === 'Escape') handleCancel();
    };

    return (
        <div className="card mt-3 config-item-card">
            <div className="card-body config-item">

                <div className="config-item__icon">
                    <i className="iconsminds-gear" />
                </div>

                <div className="config-item__key-block">
                    <div className="config-item__label">{formatLabel(config.key)}</div>
                    <div className="config-item__raw-key">{config.key}</div>
                </div>

                <div className="config-item__value-block">
                    {editing ? (
                        <>
                            {selectOptions ? (
                                <select
                                    className="config-item__edit-input config-item__edit-select"
                                    value={inputValue}
                                    onChange={e => setInputValue(e.target.value)}
                                    disabled={isThisUpdating}
                                >
                                    {selectOptions.map(opt => (
                                        <option key={opt} value={opt}>{opt}</option>
                                    ))}
                                </select>
                            ) : isPositiveInt ? (
                                <input
                                    ref={inputRef}
                                    className="config-item__edit-input"
                                    type="number"
                                    min="1"
                                    step="1"
                                    value={inputValue}
                                    onChange={e => {
                                        const val = Math.floor(Number(e.target.value));
                                        if (val >= 1) setInputValue(String(val));
                                    }}
                                    onKeyDown={handleKeyDown}
                                    disabled={isThisUpdating}
                                />
                            ) : (
                                <input
                                    ref={inputRef}
                                    className="config-item__edit-input"
                                    value={inputValue}
                                    onChange={e => setInputValue(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    disabled={isThisUpdating}
                                />
                            )}
                            {suffix && <span className="config-item__value-suffix">{suffix}</span>}
                            <button
                                className={`config-item__edit-btn config-item__edit-btn--save${isThisUpdating ? ' config-item__edit-btn--loading' : ''}`}
                                onClick={handleSave}
                                title={t('config_save')}
                            >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
                                    <polyline points="20 6 9 17 4 12" />
                                </svg>
                            </button>
                            <button
                                className="config-item__edit-btn config-item__edit-btn--cancel"
                                onClick={handleCancel}
                                title={t('config_cancel')}
                            >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        </>
                    ) : (
                        <>
                            <span className="config-item__value">
                                {config.value}{suffix}
                            </span>
                            {isEditable && (
                                <button
                                    className="config-item__edit-btn config-item__edit-trigger"
                                    onClick={handleEdit}
                                    title={t('config_edit_value')}
                                >
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                    </svg>
                                </button>
                            )}
                        </>
                    )}
                </div>

                <div className={`config-item__badge config-item__badge--${isEditable ? 'editable' : 'readonly'}`}>
                    {isEditable ? (
                        <>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="10" height="10">
                                <path d="M12 20h9" />
                                <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                            </svg>
                            {t('Editable')}
                        </>
                    ) : (
                        <>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="10" height="10">
                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                            </svg>
                            {t('Not Editable')}
                        </>
                    )}
                </div>

            </div>
        </div>
    );

};

export default React.memo(ConfigItem);
